<template>
    <v-container fluid class="pa-0 import-history-container" style="background-color: white; position: relative;">

        <v-progress-linear v-if="loading" height="4" absolute indeterminate color="primary" class="import-history-loading-bar">
        </v-progress-linear>

        <v-row no-gutters align="center">
            <v-col>
                <h2 class="ma-3">Import history</h2>
            </v-col>
            <v-col class="px-3">
                <QTextField v-model="search" placeholder="Search" externaledit singleline clearable
                    @input="onSearchInput">
                </QTextField>
            </v-col>
            <v-col cols="auto" class="px-3">
                <v-btn icon :loading="loading" small>
                    <v-icon @click="fetch()">mdi-refresh</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <Table :data="importedFiles" style="width: 100%" size="small" stripe>

            <template slot="empty">
                <EmptyPage v-if="!loading" :icon="'mdi-sleep'" :title="'No imports yet.'"
                    :message="'After you import a file, it will be listed here.'" class="ignoreodd">
                    <template v-slot:actions>
                        <slot name="empty-filtered-list-actions" />
                    </template>
                </EmptyPage>
            </template>

            <template>
                <TableColumn v-slot="{ row }" prop="Success" label="Status" width="80px">
                    <v-chip v-if="row.Success" color="success" dark x-small style="border-radius: 3px; height: 22px;">
                        Success
                    </v-chip>
                    <v-chip v-else color="error" dark x-small style="border-radius: 3px; height: 22px;">
                        Error
                    </v-chip>
                </TableColumn>
                <TableColumn v-slot="{ row }" prop="ImportDate" label="Date" width="130px">
                    {{ i18nFormatDateTime(row.ImportDate) }}
                </TableColumn>
                <TableColumn v-slot="{ row }" prop="FileName" label="Uploaded file" min-width="150px">
                    <v-btn v-if="row.ExcelFileName" color="primary" text style="text-transform: none;"
                        class="d-flex justify-start px-0"
                        @click="downloadImportedFile({ fileName: row.ExcelFileName })">
                        <v-icon size="20" class="mr-1">mdi-microsoft-excel</v-icon>
                        {{ cleanDisplayName(row.DisplayName) }}
                    </v-btn>
                    <span v-else-if="row.XmlFileName" class="text-body-2">{{ cleanDisplayName(row.DisplayName) }}</span>
                </TableColumn>
                <TableColumn v-slot="{ row }" prop="Name" label="XML" width="50px">
                    <v-btn v-if="row.XmlFileName" color="primary" icon style="text-transform: none;"
                        class="d-flex justify-start px-0" @click="downloadImportedFile({ fileName: row.XmlFileName })">
                        <v-icon size="20" class="mr-1">mdi-file-xml-box</v-icon>
                    </v-btn>
                </TableColumn>
            </template>

        </Table>

        <v-row v-if="totalCount > pageSize" no-gutters justify="center" class="py-3">
            <v-pagination
                v-model="page"
                :length="totalPages"
                :total-visible="7"
                circle
                @input="fetch"
            />
        </v-row>

    </v-container>
</template>


<script>
import ClientAPIMixin from '@/api/ClientAPIMixin';
import { Table, TableColumn } from 'element-ui';
import EmptyPage from '@/components/Sections/EmptyPage.vue';
import BlobFileMixin from "@/helpers/Mixins/BlobFileMixin";
import debounce from 'debounce';

export default {
    name: 'ImportHistory',
    props: {},
    components: {
        EmptyPage,
        Table,
        TableColumn,
    },
    mixins: [ClientAPIMixin],

    data() {
        return {
            search: "",
            importedFiles: [],
            totalCount: 0,
            page: 1,
            pageSize: 25,
            loading: false,
        };
    },

    computed: {
        totalPages() {
            return Math.ceil(this.totalCount / this.pageSize) || 1;
        },
    },

    mounted() {
        this.fetch();
        this.debouncedFetch = debounce(() => {
            this.page = 1;
            this.fetch();
        }, 300);
    },

    methods: {
        cleanDisplayName(name) {
            if (!name) return "";
            return name.replace(/\s*\(error\)\s*$/i, "").trim() || name;
        },
        onSearchInput() {
            this.debouncedFetch();
        },
        async fetch() {
            this.loading = true;
            try {
                var response = await this.getImportedFiles({
                    page: this.page,
                    pageSize: this.pageSize,
                    search: this.search || ''
                });
                this.importedFiles = response.items || [];
                this.totalCount = response.totalCount ?? 0;
            } finally {
                this.loading = false;
            }
        },

        async downloadImportedFile({ fileName, }) {
            await this.getImportedFile({ fileName })
                .then((response) => {
                    this.downloadFileResponse(response);
                });
        },

        async downloadFileResponse(response) {
            var blob = new Blob([response.data], { type: response.headers['content-type'] });
            var fileName = BlobFileMixin.methods.getFileNameFromResponseContentDisposition(response);
            await BlobFileMixin.methods.saveBlobToFile(blob, fileName);
        },
    },
};
</script>


<style lang='scss' scoped>
.import-history-container {
    min-height: 200px;
}
.import-history-loading-bar {
    z-index: 2;
}
</style>